"use client";
import { Button } from "../ui/button";
import { AspectRatio } from "../ui/aspect-ratio";
import { motion } from "motion/react";
import { Reveal } from "../reveal";
import { SectionType, ExtractBySectionType } from "types";
import { PortableText } from "next-sanity";
import { iconsMap } from "../icons";
import Link from "next/link";
import { PortableTextReactComponents } from "next-sanity";
import { SanityImage } from "../sanity-image";
import { getURL, SanityButtonType } from "@/lib/utils";

const Heading = ({ children }: { children?: React.ReactNode }) => (
  <h1 className="text-start lg:text-center font-semibold text-3xl lg:text-7xl">
    {children}
  </h1>
);

const Heading2 = ({ children }: { children?: React.ReactNode }) => (
  <h2 className="text-start lg:text-center font-medium text-2xl lg:text-6xl">
    {children}
  </h2>
);

const Heading3 = ({ children }: { children?: React.ReactNode }) => (
  <h2 className="text-start lg:text-center font-medium text-xl lg:text-5xl">
    {children}
  </h2>
);

export const ButtonComponent = ({ value }: { value: SanityButtonType }) => {
  const href = getURL(value);
  return (
    <Button
      variant={value.variant}
      radius="full"
      className="w-fit mt-4"
      size={"xl"}
      asChild={value.link ? true : false}
    >
      {value.link ? (
        <Link href={href}>
          {value.textBeforeIcon}
          {value.icon && iconsMap[value.icon]}
          {value.textAfterIcon}
        </Link>
      ) : (
        <>
          {value.textBeforeIcon}
          {value.icon && iconsMap[value.icon]}
          {value.textAfterIcon}
        </>
      )}
    </Button>
  );
};

export const components: Partial<PortableTextReactComponents> = {
  block: {
    normal: ({ children }) => (
      <p className="text-start lg:text-center max-w-screen-sm mx-auto font-medium text-lg">
        {children}
      </p>
    ),
    title: Heading,
    h1: Heading,
    h2: Heading2,
    h3: Heading3,
    h4: Heading3,
    h5: Heading3,
    h6: Heading3,
  },
  marks: {
    link: ({ children, value }) => (
      <Link href={value.href} className="no-underline text-primary-400">
        {children}
      </Link>
    ),
    strong: ({ children }) => (
      <strong className="font-medium">{children}</strong>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="ml-0 my-6 list-disc [&>li]:mt-2">{children}</ul>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="list-none flex flex-row gap-2 leading-7">
        <p className="leading-7 text-primary-text-900 dark">{children}</p>
      </li>
    ),
  },
  types: {
    button: ButtonComponent,
  },
};

type HeroSectionType = ExtractBySectionType<SectionType, "tripleHero", "_type">;

export function Hero({ data }: { data: HeroSectionType }) {
  const {
    title,
    subtitle,
    description,
    ctas,
    firstImage,
    secondImage,
    thirdImage,
  } = data;

  return (
    <section className="-top-[84px] -mb-[84px] bg-primary-900  w-full overflow-hidden relative before:content-[''] before:absolute before:left-0 before:-top-20 before:w-1/2 before:h-full before:bg-[url('/index/hero/right.svg')] before:bg-no-repeat before:bg-cover before:transform before:-scale-x-100 after:content-[''] after:absolute after:right-0 after:-top-20 after:w-1/2 after:h-full after:bg-[url('/index/hero/right.svg')] after:bg-no-repeat after:bg-cover">
      <div className="max-w-screen-2xl mx-auto overflow-hidden z-[2] relative before:absolute before:content-[''] before:w-full before:h-full before:bg-[url('/index/hero/center.svg')] before:bg-contain before:bg-center before:bg-no-repeat pt-[130px] sm:pt-[140px] before:top-0 lg:min-h-screen lg:flex lg:flex-col lg:justify-center">
        <Reveal>
          <div className="relative grid items-center justify-center text-primary-text-50 max-w-screen-2xl mx-auto gap-6 px-6">
            <PortableText value={title} components={components} />
            {subtitle && (
              <PortableText value={subtitle} components={components} />
            )}
            {description && (
              <PortableText
                value={description}
                components={{
                  block: {
                    normal: ({ children }) => (
                      <p className="text-start lg:text-center max-w-screen-sm mx-auto font-medium text-lg">
                        {children}
                      </p>
                    ),
                  },
                }}
              />
            )}
            <div className="lg:mx-auto">
              {ctas?.map((cta, index) => {
                return <ButtonComponent value={cta} key={index} />;
              })}
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-3 gap-4 lg:gap-14 max-w-screen-xl mx-auto py-10 lg:py-20 w-full">
          <motion.div
            style={{
              rotate: "5deg",
            }}
          >
            <Reveal>
              <AspectRatio ratio={1}>
                <SanityImage
                  src={firstImage}
                  alt="hero image"
                  quality={50}
                  fill
                  placeholder="blur"
                  blurDataURL={firstImage.asset?.metadata?.lqip || ""}
                  className="object-cover rounded-lg cursor-pointer"
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 33vw"
                  priority
                />
              </AspectRatio>
            </Reveal>
          </motion.div>
          <Reveal className="relative top-2 lg:top-6">
            <AspectRatio ratio={1}>
              <SanityImage
                src={secondImage}
                alt="hero image"
                fill
                priority
                placeholder="blur"
                blurDataURL={secondImage.asset?.metadata?.lqip || ""}
                className="object-cover rounded-lg cursor-pointer"
                sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 33vw"
                quality={50}
              />
            </AspectRatio>
          </Reveal>
          <motion.div
            style={{
              rotate: "-5deg",
            }}
          >
            <Reveal>
              <AspectRatio ratio={1}>
                <SanityImage
                  src={thirdImage}
                  alt="hero image"
                  fill
                  placeholder="blur"
                  blurDataURL={thirdImage.asset?.metadata?.lqip || ""}
                  className="object-cover rounded-lg cursor-pointer"
                  priority
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 33vw"
                  quality={50}
                />
              </AspectRatio>
            </Reveal>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
